import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/common/common-components/ui/Card";
import { Input } from "@/app/common/common-components/ui/Input";
import { Label } from "@/app/common/common-components/ui/Label";
import { Slider } from "@/app/common/common-components/ui/Slider";
import { formatNumber, formatInputNumber } from "@/app/common/common-functions/functions";
import { INTEREST_RATE } from "@/app/lib/constants";

interface AffordabilityCalculatorProps {
  countryCode?: string;
  currencySymbol?: string;
}

export const AffordabilityCalculator = ({ countryCode = "RSA", currencySymbol = "R" }: AffordabilityCalculatorProps) => {
  const [grossIncome, setGrossIncome] = useState("");
  const [monthlyDebt, setMonthlyDebt] = useState("");
  const [deposit, setDeposit] = useState("");
  const [interestRate, setInterestRate] = useState(INTEREST_RATE);
  const [loanTerm, setLoanTerm] = useState(20);

  // Parse numbers - remove commas before parsing
  const grossIncomeNum = parseFloat(grossIncome.replace(/,/g, "")) || 0;
  const monthlyDebtNum = parseFloat(monthlyDebt.replace(/,/g, "")) || 0;
  const depositNum = parseFloat(deposit.replace(/,/g, "")) || 0;

  // South African banks typically allow up to 30% of gross income for repayments, but not more than net surplus
  const netSurplus = grossIncomeNum - monthlyDebtNum;
  const maxMonthlyRepayment = Math.min(grossIncomeNum * 0.3, netSurplus);

  // Calculate max loan using annuity formula: P = A * [(1 - (1 + r)^-n) / r]
  const monthlyInterestRate = interestRate / 100 / 12;
  const numberOfPayments = loanTerm * 12;
  let maxLoan = 0;
  if (monthlyInterestRate > 0 && maxMonthlyRepayment > 0) {
    maxLoan = (maxMonthlyRepayment * (1 - Math.pow(1 + monthlyInterestRate, -numberOfPayments))) / monthlyInterestRate;
  }
  const maxPropertyPrice = maxLoan + depositNum;

  // Calculate actual monthly repayment for max loan
  const actualMonthlyRepayment =
    maxLoan > 0
      ? (maxLoan * (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments))) / (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1)
      : 0;

  // Debt-to-income ratio
  const dti = grossIncomeNum > 0 ? ((monthlyDebtNum + actualMonthlyRepayment) / grossIncomeNum) * 100 : 0;

  const handleNumberInput = (value: string, setter: (value: string) => void) => {
    // Only allow numbers and spaces
    const numericValue = value.replace(/[^0-9 ]/g, "");
    setter(numericValue);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Affordability Calculator</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="tw-flex tw-flex-col tw-gap-8 md:tw-flex-row">
          <div className="tw-w-full md:tw-w-1/2">
            <div className="tw-space-y-4">
              <div className="tw-space-y-2">
                <Label htmlFor="grossIncome">Gross Monthly Income ({currencySymbol})</Label>
                <Input
                  id="grossIncome"
                  type="text"
                  value={formatInputNumber(grossIncome)}
                  onChange={(e) => handleNumberInput(e.target.value, setGrossIncome)}
                  className="tw-w-full"
                />
              </div>
              <div className="tw-space-y-2">
                <Label htmlFor="monthlyDebt">Monthly Debt/Expenses ({currencySymbol})</Label>
                <Input
                  id="monthlyDebt"
                  type="text"
                  value={formatInputNumber(monthlyDebt)}
                  onChange={(e) => handleNumberInput(e.target.value, setMonthlyDebt)}
                  className="tw-w-full"
                />
              </div>
              <div className="tw-space-y-2">
                <Label htmlFor="deposit">
                  Deposit ({currencySymbol}) <span className="tw-text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="deposit"
                  type="text"
                  value={formatInputNumber(deposit)}
                  onChange={(e) => handleNumberInput(e.target.value, setDeposit)}
                  className="tw-w-full"
                />
              </div>
              <div className="tw-space-y-2">
                <Label htmlFor="interestRate">Interest Rate (%)</Label>
                <div className="tw-flex tw-items-center tw-space-x-4">
                  <Slider
                    id="interestRate"
                    min={5}
                    max={20}
                    step={0.25}
                    value={[interestRate]}
                    onValueChange={(value) => setInterestRate(value[0])}
                    className="tw-w-full"
                  />
                  <span className="tw-w-16 tw-text-right">{interestRate}%</span>
                </div>
              </div>
              <div className="tw-space-y-2">
                <Label htmlFor="loanTerm">Loan Term (Years)</Label>
                <div className="tw-flex tw-items-center tw-space-x-4">
                  <Slider id="loanTerm" min={5} max={30} step={1} value={[loanTerm]} onValueChange={(value) => setLoanTerm(value[0])} className="tw-w-full" />
                  <span className="tw-w-16 tw-text-right">{loanTerm} years</span>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-mt-8 tw-w-full md:tw-mt-0 md:tw-w-1/2">
            <div className="tw-space-y-4 tw-rounded-lg tw-bg-muted tw-p-4">
              <div className="tw-text-lg tw-font-semibold">Results</div>
              <div className="tw-flex tw-justify-between">
                <span className="tw-font-medium">Max Affordable Loan:</span>
                <span className="tw-font-semibold">
                  {currencySymbol} {formatNumber(maxLoan, 0)}
                </span>
              </div>
              <div className="tw-flex tw-justify-between">
                <span className="tw-font-medium">Max Property Price:</span>
                <span className="tw-font-semibold">
                  {currencySymbol} {formatNumber(maxPropertyPrice, 0)}
                </span>
              </div>
              <div className="tw-flex tw-justify-between">
                <span className="tw-font-medium">Estimated Monthly Repayment:</span>
                <span className="tw-font-semibold">
                  {currencySymbol} {formatNumber(actualMonthlyRepayment, 0)}
                </span>
              </div>
              <div className="tw-flex tw-justify-between">
                <span className="tw-font-medium">Debt-to-Income Ratio:</span>
                <span className="tw-font-semibold">{dti.toFixed(1)}%</span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
